import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { loginUser } from "../api/auth";
import { useAuth } from "../hooks/useAuth";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Redirect back to where they came from after login
    const from = location.state?.from?.pathname || "/my-account";

    const [formData, setFormData] = useState({ username: "", password: "" });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
        setError("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!formData.username || !formData.password) {
            setError("Please fill in all fields.");
            return;
        }

        setLoading(true);
        try {
            const data = await loginUser(formData.username, formData.password);
            login(
                {
                    email: data.user_email,
                    name: data.user_display_name,
                    nicename: data.user_nicename,
                },
                data.token,
            );
            navigate(from, { replace: true });
        } catch (err) {
            const msg =
                err.response?.data?.message || "Invalid username or password.";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 py-16">
            <div className="w-full max-w-md">
                {/* Logo / Brand */}
                <div className="text-center mb-8">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2.5"
                    >
                        <div className="w-10 h-10 bg-gradient-to-br from-secondary-500 to-secondary-700 rounded-xl flex items-center justify-center shadow-md shadow-secondary-500/20">
                            <svg
                                viewBox="0 0 24 24"
                                className="w-5 h-5 text-white"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2.2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M12 2L20 12L12 22L4 12Z" />
                                <path d="M8 12H16" />
                                <path d="M12 8V16" />
                            </svg>
                        </div>
                        <span className="text-2xl font-extrabold tracking-tight">
                            <span className="text-neutral-900">Fits</span>
                            <span className="text-secondary-600">Aura</span>
                        </span>
                    </Link>
                    <h1 className="text-xl font-bold text-neutral-900 mt-6">
                        Welcome back
                    </h1>
                    <p className="text-neutral-500 text-sm mt-1">
                        Sign in to your account
                    </p>
                </div>

                {/* Card */}
                <div className="card p-8">
                    {/* Error Alert */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-6">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} noValidate>
                        <div className="form-group">
                            <label className="label" htmlFor="username">
                                Username or Email
                            </label>
                            <input
                                id="username"
                                name="username"
                                type="text"
                                className="input"
                                placeholder="your@email.com"
                                value={formData.username}
                                onChange={handleChange}
                                autoComplete="username"
                            />
                        </div>

                        <div className="form-group">
                            <div className="flex items-center justify-between mb-1">
                                <label
                                    className="label mb-0"
                                    htmlFor="password"
                                >
                                    Password
                                </label>

                                <a
                                    href="/wp-login.php?action=lostpassword"
                                    className="text-xs text-primary-600 hover:underline"
                                >
                                    Forgot password?
                                </a>
                            </div>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                className="input"
                                placeholder="••••••••"
                                value={formData.password}
                                onChange={handleChange}
                                autoComplete="current-password"
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn-primary w-full mt-2"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Signing in...
                                </>
                            ) : (
                                "Sign In"
                            )}
                        </button>
                    </form>
                </div>

                {/* Register link */}
                <p className="text-center text-sm text-neutral-500 mt-6">
                    Don't have an account?{" "}
                    <Link
                        to="/register"
                        className="text-primary-600 font-semibold hover:underline"
                    >
                        Create one
                    </Link>
                </p>
            </div>
        </div>
    );
}
